import { Icons } from "assets/icons";
import DetailsTitle from "components/common/detailsPage/DetailsTitle";
import InfoItem from "components/common/detailsPage/InfoItem";
import NoDataFound from "components/common/NoDataFound";
import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import { StatusBadge } from "components/common/StatusBadge";
import DataTable from "components/common/table/DataTable";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getRoleDetails, getRolesPermissions } from "store/slices/rolesSlice";
import { formatUTCDate } from "utils/utils";

const RoleDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const id = useQueryParam("id");
  const { noUsers } = Icons || {};

  const { canAction } = usePermission();
  const { canView } = canAction("roles");

  const { loading, itemDetails, permissions } = useSelector(
    (state) => state.Roles,
  );
  const [selected, setSelected] = useState({});

  const { name, nameAr, hasFullPermission, status, createdAt, assignedUsers } =
    itemDetails || {};

  useEffect(() => {
    dispatch(getRolesPermissions());
  }, []);

  useEffect(() => {
    if (id && canView) {
      dispatch(getRoleDetails(id));
    }
  }, [id, canView]);

  useEffect(() => {
    if (!itemDetails || !permissions?.length) return;

    const selectedMap = {};

    // initialize all modules with empty array
    permissions?.forEach((module) => {
      selectedMap[module.module] = [];
    });

    // fill selected permissions from itemDetails
    itemDetails?.permissions?.forEach((mod) => {
      if (selectedMap[mod.module]) {
        selectedMap[mod.module] = [...mod.permissions];
      }
    });

    setSelected(selectedMap);
  }, [itemDetails, permissions]);

  const columns = useMemo(
    () => [
      {
        header: t("Name"),
        key: "name",
      },
      {
        header: t("Email"),
        key: "email",
      },
      {
        header: t("Phone Number"),
        key: "phoneNumber",
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <StatusBadge status={value} t={t} />,
      },
    ],
    [],
  );
  const shimmerLayout1 = [
    {
      col: 12,
      blocks: [
        { rows: 2, cols: 2 },
      ],
    },
    {
      col: 12,
      blocks: [{ rows: 5, cols: 2 }],
    },
  ];
    const shimmerLayout2 = [
    {
      col: 12,
      blocks: [
        { rows: 1, cols: 5 },
      ],
    }
  ];

  if (!canView) return null;

  if (loading) {
    return (
      <div className="page-content">
        <DetailsTitle title={t("Role Details")} />
        <Row>
          <Col lg={7}>
            <DetailsShimmer layout={shimmerLayout1} />
          </Col>
          <Col lg={5}>
            <DetailsShimmer className="overflow-x-auto"  layout={shimmerLayout2}/>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="page-content common_details_page role_details_page">
      <DetailsTitle title={t("Role Details")} />
      <Row>
        <Col lg={7}>
          <div className="common_card">
            <h2 className="title_20 mb-2">{t("General Details")}</h2>
            <Row>
              <InfoItem
                label={t("Name")}
                value={language ? nameAr : name}
                col={{ lg: 6 }}
                classname="mt-3"
              />
              <InfoItem
                label={t("Has Full Permission")}
                value={hasFullPermission ? t("Yes") : t("No")}
                col={{ lg: 6 }}
                classname="mt-3"
              />
              <InfoItem
                label={t("Status")}
                value={<StatusBadge status={status} t={t} />}
                col={{ lg: 6 }}
                classname="mt-3"
              />
              <InfoItem
                label={t("Created Date")}
                value={formatUTCDate(createdAt)}
                col={{ lg: 6 }}
                classname="mt-3"
              />
            </Row>
          </div>
          {!hasFullPermission && (
            <div className="common_card">
              <Row>
                <h2 className="title_20">{t("Permissions Details")}</h2>

                {permissions?.map((module) => (
                  <Col md={6} key={module.id} className="">
                    <div className="single_permission">
                      <h6>{t(module.label)}</h6>

                      <div className="permission_wrapper">
                        {module?.permission?.map((perm) => (
                          <Form.Check
                            key={perm.id}
                            type="checkbox"
                            label={t(perm.label)}
                            checked={
                              selected[module.module]?.includes(perm.key) ||
                              false
                            }
                          />
                        ))}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Col>
        <Col lg={5}>
          <div className="common_card">
            <h2 className="title_20 mb-4">{t("Assigned Users")}</h2>
            {assignedUsers?.length === 0 ? (
              <NoDataFound title={t("No users assigned yet")} icon={noUsers} />
            ) : (
              <DataTable columns={columns} data={assignedUsers} t={t} />
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default RoleDetails;
